import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Alert, Button, Container, Form, Modal, Spinner } from 'react-bootstrap';
import { AdminApiDomain, getAdminApiUrl } from '../../../config/ApiRouting';
import {
  FulfillmentAdminClient,
  UNRECOGNIZED_SCAN_INBOUND_NO_LIMIT,
  UNRECOGNIZED_SCAN_INBOUND_NO_PATTERN,
  UNRECOGNIZED_SCAN_INBOUND_REASON,
  UNRECOGNIZED_SCAN_MEMO_LIMIT,
  UNRECOGNIZED_SCAN_MEMO_REASON,
  UNRECOGNIZED_SCAN_REMOVAL_REASON_LABELS,
  UNRECOGNIZED_SCAN_REMOVAL_REASONS,
  type FulfillmentOrderDetail,
  type UnrecognizedScan,
  type UnrecognizedScanCandidatePage,
  type UnrecognizedScanRemovalReason,
  type UnrecognizedScanNumberMatch,
  type UnrecognizedScanSuspectedOrder,
} from '../../../infrastructure/FulfillmentAdminClient';
import { ROUTES } from '../../../routes/routes';
import { kstTimestamp } from '@/utils/fulfillmentTime';
import { FulfillmentToastHost } from '@/pages/fulfillment/components/FulfillmentToast';
import { useFulfillmentToast } from '@/pages/fulfillment/components/useFulfillmentToast';
import { serverRefusal } from '@/utils/fulfillmentErrors';
import { trapTabFocus } from '@/pages/fulfillment/components/trapTabFocus';
import { CloseIcon, RefreshIcon } from '@/pages/fulfillment/components/UltimateIcons';
import { ULTIMATE_MODAL } from '@/pages/fulfillment/components/ultimateModal';
import '../ultimateDesign.css';
import './unrecognizedScans.css';

const GENERIC_ERROR = 'Could not load the unrecognized scans.';
const ACTION_ERROR = 'The request failed. Please try again shortly.';
/** A 409 is not a system failure: someone else already matched or removed the entry. */
const CONFLICT_ERROR = 'The pool has been reloaded.';
/** Same conflict, but the rows on screen are the stale ones the 409 just disproved. */
const CONFLICT_STALE_ERROR = 'The pool could not be reloaded, so these rows may be out of date.';
/** [BR-24/BR-4] the cell names both ways out, because the doctrine here is that a missing candidate is bad data upstream. */
const NO_CANDIDATE_TEXT =
  'No Processing order contains this product — check the order data upstream, or remove with a reason';
/** The lookup itself failed, so an empty candidate list says nothing about the order data. */
const CANDIDATES_UNAVAILABLE_TEXT =
  'Candidate orders could not be looked up just now — reload before judging this entry, or remove with a reason';
/** Why Review & Match is unpressable, said on the control itself rather than only in a distant cell. */
const NO_CANDIDATE_HINT = 'No candidate order to match against yet';
const CANDIDATES_UNAVAILABLE_HINT = 'Candidate orders could not be looked up just now';
/** A link names an entry by id, and an entry leaves the pool once it is matched or removed. */
const LINKED_ROW_GONE = 'The linked entry is no longer in the pool — it has already been matched or removed.';
const INBOUND_NO_ERROR = 'Inbound No. must be 12–20 digits (YYYYMMDD then that day’s sequence)';
/** What is rendered where the parcel declared nothing, so an empty cell can never read as data. */
const ABSENT = '—';

/** What a typed order number must look like: the order id the candidates and Order Detail name orders by. */
const ORDER_NUMBER_PATTERN = /^\d{1,12}$/;

/** How many candidates one Show more reads; the server accepts up to 100. */
const CANDIDATE_PAGE_SIZE = 50;
const CANDIDATES_PAGE_ERROR =
  'The other candidates could not be loaded just now — type the order number below instead.';

/** One page of the pool; the server caps it at the same number, and the cursor reaches the rest. */
const PAGE_SIZE = 100;

/** How often the pool is re-read while the tab is in front (SSD-7209, decision D6); a hidden tab waits. */
export const POOL_REFRESH_MS = 30_000;

interface AdminActionErrorBody {
  message?: string | string[];
}

type LoadResult = 'applied-success' | 'applied-failure' | 'superseded';

/** The removal being composed: no reason yet means the dialog is open but the confirm is not armed. */
interface RemovalDraft {
  scan: UnrecognizedScan;
  /** Every open row of the parcel when the whole box is leaving; the one row otherwise. */
  rows: UnrecognizedScan[];
  reason: UnrecognizedScanRemovalReason | null;
  inboundNo: string;
  memo: string;
}

const failureOf = (err: unknown) => (isAxiosError<AdminActionErrorBody>(err) ? err : undefined);

/** How the pool names one parcel's goods; the sku is the last resort, never a blank. */
const productLabelOf = (scan: UnrecognizedScan): string =>
  [scan.brand, scan.productName ?? scan.sku ?? scan.code].filter(Boolean).join(' ');

/** The DOM anchor of one pool row, so a deep link (`#poolrow-{id}`) or QA can target the entry by its server id. */
const poolRowDomId = (scanId: string) => `poolrow-${scanId}`;

/** The parcel a row belongs to: the one it was parked under, or itself on a row parked before parcels existed. */
const parcelIdOf = (scan: UnrecognizedScan): string => scan.parcelId ?? scan.id;

/** The rows of one box, as the page lists them. */
interface Parcel {
  id: string;
  rows: UnrecognizedScan[];
}

/**
 * Rows grouped back into the boxes they were parked off, in the order the page lists them. The server pages
 * parcel by parcel, so a parcel is never split across pages and the grouping never sees half a box.
 */
/** Whether one row carries the typed text anywhere a person would read it: waybill, order, product, sku, code, memo or shelf. */
const matchesFilter = (scan: UnrecognizedScan, filter: string): boolean => {
  const needle = filter.trim().toLowerCase();
  return [
    scan.trackingNo,
    scan.orderNo,
    scan.brand,
    scan.productName,
    scan.productNameKr,
    scan.sku,
    scan.code,
    scan.memo,
    scan.temporaryLocation,
  ]
    .filter((value): value is string => typeof value === 'string')
    .some((value) => value.toLowerCase().includes(needle));
};

const parcelsOf = (scans: UnrecognizedScan[]): Parcel[] => {
  const parcels: Parcel[] = [];
  const byId = new Map<string, Parcel>();
  for (const scan of scans) {
    const id = parcelIdOf(scan);
    let parcel = byId.get(id);
    if (!parcel) {
      parcel = { id, rows: [] };
      byId.set(id, parcel);
      parcels.push(parcel);
    }
    parcel.rows.push(scan);
  }
  return parcels;
};

/** Reads the row a URL hash names; a pool id may hold characters (`#`) that reach the hash percent-encoded. */
const poolRowDomIdOfHash = (hash: string): string | null => {
  try {
    const target = decodeURIComponent(hash.replace(/^#/, ''));
    return target.startsWith('poolrow-') ? target : null;
  } catch {
    return null;
  }
};

/** How many candidates the server left out of the capped list; nothing is claimed when it did not say. */
const hiddenCandidateCountOf = (scan: UnrecognizedScan): number =>
  Math.max(0, (scan.suspectedOrdersTotal ?? 0) - (scan.suspectedOrders?.length ?? 0));

/** How many proposals a row prints before it hands over to Review & Match; past a few the desk is reading, not confirming. */
const CANDIDATES_SHOWN_IN_ROW = 3;

/** Which of the parcel's numbers someone registered on an order line, as the row says it (SSD-7442). */
const NUMBER_SOURCE_LABELS: Record<UnrecognizedScanNumberMatch['matchedBy'], string> = {
  PURCHASE_ORDER: 'order number',
  WAYBILL: 'waybill',
};

/**
 * What the parcel's own numbers say now, in the row (SSD-7442): someone registered its order number or waybill on an
 * order line after the box was parked — from the gig app, the Coupang extension or by hand — so the line is no longer
 * a guess. Only a single live line carrying the product is offered as a one-press match; the rest only point.
 */
const numberMatchText = (match: UnrecognizedScanNumberMatch): string => {
  const source = NUMBER_SOURCE_LABELS[match.matchedBy];
  const where =
    match.orderId !== null
      ? `Order ${match.orderId}${match.lineNo !== null ? ` · line ${match.lineNo}` : ''}`
      : 'an order';
  switch (match.status) {
    case 'MATCHED':
      return `Its ${source} is now on ${where}.`;
    case 'MULTIPLE':
      return `Its ${source} is on ${match.lineCount} lines with this product. Pick one in Review & Match.`;
    case 'PRODUCT_DIFFERS':
      return `Its ${source} is on ${where}, but not with this product. Check it in Review & Match.`;
    default:
      return `Its ${source} is on ${where}, which is already finished.`;
  }
};

/** The candidates a row prints, and how many it leaves to the dialog — the server's own leftovers counted in. */
const candidatesForRow = (scan: UnrecognizedScan): { shown: UnrecognizedScanSuspectedOrder[]; more: number } => {
  const all = scan.suspectedOrders ?? [];
  const shown = all.slice(0, CANDIDATES_SHOWN_IN_ROW);
  return { shown, more: Math.max(0, (scan.suspectedOrdersTotal ?? all.length) - shown.length) };
};

/**
 * Every sku the entry's goods can travel under: the scanned code, the sku the desk declared, and the skus
 * the server proposed candidates by — which carry the parent sku a variation ships under.
 */
const productSkusOf = (scan: UnrecognizedScan): string[] => [
  ...new Set(
    [
      // A parcel registered off its waybill scanned no product code, so the waybill is not a sku to look for.
      ...(scan.codeKind === 'WAYBILL' ? [] : [scan.code]),
      scan.sku,
      ...(scan.suspectedOrders ?? []).map((candidate) => candidate.sku),
    ].filter((sku): sku is string => typeof sku === 'string' && sku !== ''),
  ),
];

/** One line of a typed order read as a candidate, so it is matched exactly the way a proposed one is. */
const candidateOfLine = (
  order: FulfillmentOrderDetail,
  line: FulfillmentOrderDetail['lines'][number],
): UnrecognizedScanSuspectedOrder => ({
  orderId: order.header.orderId,
  wcStatus: order.header.wcStatus,
  lineNo: line.lineNo,
  sku: line.sku,
  pic: order.header.picUser,
  channel: order.header.channel,
  fulfillmentStatus: order.header.fulfillmentStatus,
  lineQty: line.qtyOrdered,
});

/** The candidates read past the pool row's handful: the pages so far, the whole count, and where the next page starts. */
interface PagedCandidates {
  candidates: UnrecognizedScanSuspectedOrder[];
  total: number;
  nextCursor: string | null;
}

/** Appends a page, dropping any line already shown — a line can shift pages while the desk reads. */
const withPage = (shown: UnrecognizedScanSuspectedOrder[], page: UnrecognizedScanCandidatePage): PagedCandidates => {
  const seen = new Set(shown.map((candidate) => `${candidate.orderId}-${candidate.lineNo}`));
  return {
    candidates: [
      ...shown,
      ...page.suspectedOrders.filter((candidate) => !seen.has(`${candidate.orderId}-${candidate.lineNo}`)),
    ],
    total: page.suspectedOrdersTotal,
    nextCursor: page.nextCursor ?? null,
  };
};

/** How the count in the parcel compares with what a candidate line wants: the one number a match is judged on. */
type QuantityFit =
  | { kind: 'same' }
  | { kind: 'short'; by: number }
  | { kind: 'over'; by: number }
  | { kind: 'unknown' };

const quantityFitOf = (inParcel: number | null | undefined, onLine: number | null | undefined): QuantityFit => {
  if (typeof inParcel !== 'number' || typeof onLine !== 'number') {
    return { kind: 'unknown' };
  }
  if (inParcel === onLine) {
    return { kind: 'same' };
  }
  return inParcel < onLine ? { kind: 'short', by: onLine - inParcel } : { kind: 'over', by: inParcel - onLine };
};

/** The fit as a chip: green when the parcel fills the line exactly, amber when it is short or over, a dash when a count is missing. */
function FitChip({ fit }: { fit: QuantityFit }) {
  switch (fit.kind) {
    case 'same':
      return <span className="pool-chip pool-chip-live">same count</span>;
    case 'short':
      return <span className="pool-chip pool-chip-warn">{fit.by} short</span>;
    case 'over':
      return <span className="pool-chip pool-chip-warn">{fit.by} over</span>;
    default:
      return <span className="text-muted">{ABSENT}</span>;
  }
}

/**
 * The box a dialog is about, in one card: the goods, the facts declared at the scan desk, and the count in hand as a
 * number large enough to be checked against a line's count at a glance. A whole parcel lists its products instead.
 */
function ParcelCard({ scan, rows, testId }: { scan: UnrecognizedScan; rows?: UnrecognizedScan[]; testId?: string }) {
  const parcelRows = rows && rows.length > 1 ? rows : null;
  // A whole parcel's count is the sum of its products' counts; one product without a count leaves the sum unknown.
  const units = parcelRows
    ? parcelRows.every((row) => typeof row.quantity === 'number')
      ? parcelRows.reduce((sum, row) => sum + (row.quantity ?? 0), 0)
      : null
    : scan.quantity;
  return (
    <div className="pool-card" data-testid={testId}>
      <div className="pool-card-main">
        {parcelRows ? (
          <>
            <div className="pool-card-name">{`${parcelRows.length} products under ${scan.trackingNo ?? scan.code}`}</div>
            <ul className="pool-card-list">
              {parcelRows.map((row) => (
                <li key={row.id}>
                  {/* No count declared reads as none, as in the Qty column, never as one. */}
                  {productLabelOf(row)} ×{row.quantity ?? ABSENT}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <>
            <div className="pool-card-name">
              {scan.brand && <b>{scan.brand} </b>}
              {scan.productName ?? scan.sku ?? scan.code}
            </div>
            {(scan.productNameKr || scan.size) && (
              <div className="pool-card-sub">{[scan.productNameKr, scan.size].filter(Boolean).join(' · ')}</div>
            )}
          </>
        )}
        <div className="pool-card-meta">
          Barcode <code>{scan.code}</code> · Tracking {scan.trackingNo ?? ABSENT} · Order No {scan.orderNo ?? ABSENT} ·
          Registered by {scan.operator ?? ABSENT} {kstTimestamp(scan.occurredAt)}
        </div>
        {scan.memo && <div className="pool-memo">“{scan.memo}”</div>}
      </div>
      <div className="pool-card-qty">
        <span className="pool-card-count">{units ?? ABSENT}</span>{' '}
        <span className="pool-card-count-label">in this parcel</span>
      </div>
    </div>
  );
}

/** The column heads of a candidate table; the fit column names the parcel's own count so the chips read against it. */
function CandidateHead({ parcelQty }: { parcelQty: number | null | undefined }) {
  return (
    <thead>
      <tr>
        <th>Order</th>
        <th>PIC</th>
        <th>Line</th>
        <th>Line qty</th>
        <th>{typeof parcelQty === 'number' ? `vs ${parcelQty} in parcel` : 'vs parcel'}</th>
        <th>
          <span className="visually-hidden">Action</span>
        </th>
      </tr>
    </thead>
  );
}

/** Proposed or typed lines as rows: the order and its state, who owns it, the line, what it wants, and the fit. */
function CandidateRows({
  scan,
  candidates,
  disabled,
  onMatch,
}: {
  scan: UnrecognizedScan;
  candidates: UnrecognizedScanSuspectedOrder[];
  disabled: boolean;
  onMatch: (candidate: UnrecognizedScanSuspectedOrder) => void;
}) {
  return (
    <>
      {candidates.map((candidate) => (
        <tr key={`${candidate.orderId}-${candidate.lineNo}`}>
          <td>
            <Link to={ROUTES.fulfillment.orderDetail(candidate.orderId)} className="rm-order-id">
              {candidate.orderId}
            </Link>
            <div className="rm-order-sub">
              <span>{candidate.channel ?? ABSENT}</span>
              {/* The shop status says nothing about what the warehouse did with the order [D]. */}
              <span className="pool-chip pool-chip-muted">
                {candidate.fulfillmentStatus ?? candidate.wcStatus ?? 'unknown'}
              </span>
            </div>
          </td>
          <td className="rm-pic">{candidate.pic ?? <span className="text-muted">{ABSENT}</span>}</td>
          <td className="rm-line">line {candidate.lineNo}</td>
          <td className="rm-qty">
            {/* The units the line wants, never the parcel's own count; a line that reports none shows none. */}
            {typeof candidate.lineQty === 'number' ? (
              <>
                <span className="rm-count">×{candidate.lineQty}</span>
                <span className="rm-count-label"> on the line</span>
              </>
            ) : (
              <span className="text-muted">{ABSENT}</span>
            )}
          </td>
          <td className="rm-fit">
            <FitChip fit={quantityFitOf(scan.quantity, candidate.lineQty)} />
          </td>
          <td className="rm-act">
            <Button
              size="sm"
              variant="success"
              disabled={disabled}
              aria-label={`Match ${scan.code} to order ${candidate.orderId} line ${candidate.lineNo}`}
              onClick={() => onMatch(candidate)}
            >
              Match to this line
            </Button>
          </td>
        </tr>
      ))}
    </>
  );
}

/** Where a row sits in its box, for the dialog's subtitle: nothing on a box of one. */
const positionInParcel = (scan: UnrecognizedScan, parcel: Parcel | undefined): string =>
  parcel && parcel.rows.length > 1
    ? ` · product ${parcel.rows.findIndex((row) => row.id === scan.id) + 1} of ${parcel.rows.length}`
    : '';

/**
 * The candidates Review & Match offers (SSD-7208). The pool row carries only the server's handful; past it the
 * desk pages through every candidate, newest order first, until the cursor runs out. The first page replaces
 * the handful rather than following it, because the two are ordered differently and the paged list is whole.
 */
function ReviewCandidates({
  scan,
  client,
  candidatesUnavailable,
  disabled,
  onMatch,
}: {
  scan: UnrecognizedScan;
  client: FulfillmentAdminClient;
  candidatesUnavailable: boolean;
  disabled: boolean;
  onMatch: (candidate: UnrecognizedScanSuspectedOrder) => void;
}) {
  const [paged, setPaged] = useState<PagedCandidates | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  const shown = paged?.candidates ?? scan.suspectedOrders ?? [];
  const hidden = paged ? Math.max(0, paged.total - shown.length) : hiddenCandidateCountOf(scan);
  const hasMore = paged ? paged.nextCursor !== null : hidden > 0;

  const showMore = async () => {
    if (loadingMore) {
      return;
    }
    setLoadingMore(true);
    setPageError(null);
    try {
      const page = await client.listUnrecognizedScanCandidates(scan.id, {
        limit: CANDIDATE_PAGE_SIZE,
        ...(paged?.nextCursor ? { cursor: paged.nextCursor } : {}),
      });
      setPaged(withPage(paged?.candidates ?? [], page));
    } catch (err) {
      setPageError(serverRefusal(err) ?? CANDIDATES_PAGE_ERROR);
    } finally {
      setLoadingMore(false);
    }
  };

  const total = paged ? paged.total : scan.suspectedOrdersTotal;

  return (
    <div className="rm-section">
      <div className="rm-section-head">
        <span className="rm-section-title">Processing orders with this product</span>
        {shown.length > 0 && (
          <span className="rm-section-count">
            {shown.length} shown{typeof total === 'number' && total > shown.length ? ` of ${total}` : ''}
          </span>
        )}
      </div>
      <p className="rm-section-sub">
        Only Processing orders that carry this product are proposed, so a match is a confirmation rather than a search.
        The waybill lands on the order line you pick, not on the whole order.
      </p>
      <table className="rm-table">
        <CandidateHead parcelQty={scan.quantity} />
        <tbody>
          <CandidateRows scan={scan} candidates={shown} disabled={disabled} onMatch={onMatch} />
          {shown.length === 0 && (
            <tr>
              <td colSpan={6} className="text-muted py-3">
                {candidatesUnavailable ? CANDIDATES_UNAVAILABLE_TEXT : NO_CANDIDATE_TEXT}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {hasMore && (
        <div className="rm-more">
          <span>
            +{hidden} more Processing {hidden === 1 ? 'order carries' : 'orders carry'} this product.
          </span>
          <Button size="sm" variant="outline-primary" disabled={loadingMore} onClick={() => void showMore()}>
            {loadingMore ? 'Loading…' : 'Show more candidates'}
          </Button>
        </div>
      )}
      {pageError && (
        <Alert variant="danger" className="py-2 mt-2 mb-0 small">
          {pageError}
        </Alert>
      )}
    </div>
  );
}

/** What the typed-order lookup holds: nothing yet, the order's lines with this product, or why there are none. */
type OrderLookup =
  | { state: 'idle' }
  | { state: 'loading'; orderId: number }
  | { state: 'found'; candidates: UnrecognizedScanSuspectedOrder[] }
  | { state: 'refused'; reason: string };

/**
 * Matching to an order the server did not propose (SSD-7208). The proposals stop at a handful, so the order
 * in hand may be one of the rest: the handler types its number and is shown only that order's lines carrying
 * this product. An order with no such line is refused here by name, and everything else — a closed order, a
 * waybill already on another line — is still the server's to refuse, through the same match a proposal uses.
 */
function OrderNumberMatch({
  scan,
  client,
  disabled,
  onMatch,
}: {
  scan: UnrecognizedScan;
  client: FulfillmentAdminClient;
  disabled: boolean;
  onMatch: (candidate: UnrecognizedScanSuspectedOrder) => void;
}) {
  const [typed, setTyped] = useState('');
  const [lookup, setLookup] = useState<OrderLookup>({ state: 'idle' });
  const lookupRequestId = useRef(0);

  const findOrder = async (event: FormEvent) => {
    event.preventDefault();
    const orderNumber = typed.trim();
    if (!ORDER_NUMBER_PATTERN.test(orderNumber)) {
      setLookup({ state: 'refused', reason: 'Type the order number in digits only, as Order Detail shows it.' });
      return;
    }
    const orderId = Number(orderNumber);
    const requestId = ++lookupRequestId.current;
    setLookup({ state: 'loading', orderId });
    try {
      const order = await client.getOrderDetail(orderId);
      if (requestId !== lookupRequestId.current) {
        return;
      }
      const skus = productSkusOf(scan);
      const candidates = order.lines
        .filter((line) => skus.includes(line.sku))
        .map((line) => candidateOfLine(order, line));
      setLookup(
        candidates.length > 0
          ? { state: 'found', candidates }
          : {
              state: 'refused',
              reason: `Order ${orderId} has no line with this product (${skus.join(' / ')}), so this parcel cannot be matched to it. Check the order number on the parcel or the packing slip.`,
            },
      );
    } catch (err) {
      if (requestId !== lookupRequestId.current) {
        return;
      }
      setLookup({
        state: 'refused',
        reason:
          failureOf(err)?.response?.status === 404
            ? `No order ${orderId} was found. Check the number and try again.`
            : (serverRefusal(err) ?? `Order ${orderId} could not be looked up just now. Try again shortly.`),
      });
    }
  };

  return (
    <div className="rm-section" data-testid="order-number-match">
      <div className="rm-section-head">
        <span className="rm-section-title">Match to another order</span>
      </div>
      <p className="rm-section-sub">
        The order you want is not listed? Type its order number to see its lines with this product.
      </p>
      <Form className="rm-find" onSubmit={(event) => void findOrder(event)}>
        <Form.Control
          size="sm"
          inputMode="numeric"
          aria-label={`Order number to match ${scan.code} to`}
          placeholder="Order number, e.g. 380371"
          value={typed}
          onChange={(event) => {
            setTyped(event.target.value);
            // A new number voids what the last one found, so its lines can never be matched under this one.
            lookupRequestId.current += 1;
            setLookup({ state: 'idle' });
          }}
        />
        <Button size="sm" type="submit" variant="outline-primary" disabled={lookup.state === 'loading'}>
          Find order
        </Button>
        {lookup.state === 'loading' && <Spinner animation="border" size="sm" />}
      </Form>
      {lookup.state === 'refused' && (
        <Alert variant="danger" className="py-2 mb-0 mt-2 small" role="alert">
          {lookup.reason}
        </Alert>
      )}
      {lookup.state === 'found' && (
        <table className="rm-table mt-2">
          <CandidateHead parcelQty={scan.quantity} />
          <tbody>
            <CandidateRows scan={scan} candidates={lookup.candidates} disabled={disabled} onMatch={onMatch} />
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * What a match did beyond the headline, read off the row the handler was looking at. It states only what
 * the resolve is known to do: the server sends no notification, so "notified" is never claimed here.
 */
const matchOutcomeOf = (scan: UnrecognizedScan, candidate: UnrecognizedScanSuspectedOrder): string[] => [
  scan.trackingNo
    ? `Tracking ${scan.trackingNo} registered on line ${candidate.lineNo}`
    : `No tracking number was on the scan, so line ${candidate.lineNo} carries none`,
  'Removed from the pool',
  ...(scan.operator ? [`Registered by ${scan.operator}`] : []),
];

export default function FulfillmentUnrecognizedScansPage() {
  // Slack and the comment hub link one entry as `#poolrow-{id}`; the row it names is scrolled to and marked.
  const { hash, search } = useLocation();
  const linkedRowDomId = poolRowDomIdOfHash(hash);
  /** The hash already acted on, so a reload does not yank the screen back to the linked row. */
  const anchoredHash = useRef<string | null>(null);
  /** The hash whose entry the whole pool was read without finding. */
  const [goneHash, setGoneHash] = useState<string | null>(null);
  const client = useMemo(() => new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);
  // One feedback slot for this screen, the same component every desk screen renders [T03].
  const { toasts, showSuccess, dismiss } = useFulfillmentToast();
  const [scans, setScans] = useState<UnrecognizedScan[]>([]);
  const [openTotal, setOpenTotal] = useState<number | null>(null);
  const [openParcels, setOpenParcels] = useState<number | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [paging, setPaging] = useState(false);
  const [candidatesUnavailable, setCandidatesUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** A failed read is not an empty pool: the table, the counters and the retry all key off this. */
  const [readFailed, setReadFailed] = useState(false);
  const [removal, setRemoval] = useState<RemovalDraft | null>(null);
  // A link from the Slack thread opens the pool already narrowed to its parcel (`?q=<waybill>`, SSD-7442).
  const [filter, setFilter] = useState(() => new URLSearchParams(search).get('q') ?? '');
  const [reviewing, setReviewing] = useState<UnrecognizedScan | null>(null);
  const loadRequestId = useRef(0);
  /** Whether a later page has been appended; a poll must not collapse the pool back to its first page under the operator. */
  const pagedRef = useRef(false);
  /** Reads still out, so a poll tick never starts a second one behind them. */
  const readsOutRef = useRef(0);
  /** Whether a next page is being fetched; a quiet read that lands meanwhile must not collapse the pool under it. */
  const pagingRef = useRef(false);
  /** Whether the error on screen is a refused Match or Remove, which only the operator's next action may clear. */
  const actionErrorShownRef = useRef(false);

  /**
   * Answers whether the rows on screen are the server's, so a caller never reports a refresh that did not happen.
   * A quiet read is the 30 s beat: it swaps the rows in without the spinner and on a failure keeps the rows it had
   * rather than flagging the pool unread. A read nobody asked for (the beat, or the tab coming back) leaves a refused
   * action's message on screen for the operator to read; only their next action or their own refresh clears it.
   */
  const load = useCallback(
    async ({
      quiet = false,
      keepRefusal = quiet,
    }: { quiet?: boolean; keepRefusal?: boolean } = {}): Promise<LoadResult> => {
      const requestId = ++loadRequestId.current;
      readsOutRef.current += 1;
      if (!quiet) {
        setLoading(true);
      }
      try {
        const pool = await client.listUnrecognizedScans({ limit: PAGE_SIZE });
        if (requestId === loadRequestId.current) {
          if (quiet && (pagedRef.current || pagingRef.current)) {
            // The operator paged in while the beat was out: their rows win, and the beat waits for the first page.
            return 'superseded';
          }
          pagedRef.current = false;
          setScans(pool.scans);
          setOpenTotal(pool.openTotal ?? null);
          setOpenParcels(pool.openParcels ?? null);
          setNextCursor(pool.nextCursor ?? null);
          setCandidatesUnavailable(pool.suspectedOrdersUnavailable === true);
          if (!keepRefusal || !actionErrorShownRef.current) {
            actionErrorShownRef.current = false;
            setError(null);
          }
          setReadFailed(false);
          return 'applied-success';
        }
        return 'superseded';
      } catch {
        if (requestId === loadRequestId.current) {
          if (!quiet) {
            actionErrorShownRef.current = false;
            setError(GENERIC_ERROR);
            setReadFailed(true);
          }
          return 'applied-failure';
        }
        return 'superseded';
      } finally {
        readsOutRef.current -= 1;
        if (requestId === loadRequestId.current) {
          setLoading(false);
        }
      }
    },
    [client],
  );

  /** The next page is appended, never swapped in: paging must not take away the rows being worked through. */
  const loadMore = useCallback(async () => {
    if (!nextCursor || paging) {
      return;
    }
    setPaging(true);
    pagingRef.current = true;
    try {
      const pool = await client.listUnrecognizedScans({ limit: PAGE_SIZE, cursor: nextCursor });
      pagedRef.current = true;
      setScans((current) => [...current, ...pool.scans]);
      setOpenTotal(pool.openTotal ?? null);
      setOpenParcels(pool.openParcels ?? null);
      setNextCursor(pool.nextCursor ?? null);
    } catch {
      // A read failure replaces a refused action's message, so the next good read may clear it.
      actionErrorShownRef.current = false;
      setError(GENERIC_ERROR);
    } finally {
      pagingRef.current = false;
      setPaging(false);
    }
  }, [client, nextCursor, paging]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Rows arrive after the browser's own hash scroll has run, so the linked row is brought into view once it
   * renders — once per hash, not on every reload. A link may name an entry past the first page, so the pool
   * is paged through until the entry shows up or the cursor runs out; an error stops the paging rather than
   * retrying it in a loop.
   */
  useEffect(() => {
    if (!linkedRowDomId || anchoredHash.current === hash || loading || paging || error !== null) {
      return;
    }
    const row = document.getElementById(linkedRowDomId);
    if (row) {
      anchoredHash.current = hash;
      row.scrollIntoView({ block: 'center' });
      row.focus({ preventScroll: true });
      return;
    }
    if (nextCursor) {
      void loadMore();
      return;
    }
    anchoredHash.current = hash;
    setGoneHash(hash);
  }, [hash, linkedRowDomId, scans, loading, paging, error, nextCursor, loadMore]);

  // The scan desk registers into this pool all day, so a tab coming back to the front is a stale one [D-p].
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        // Coming back from the scan desk must not wipe a refusal the operator has not read.
        void load({ keepRefusal: true });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  // And a tab that stays in front is re-read on the beat (SSD-7209, decision D6), unless a read is still in
  // flight, a Match or Remove is out, a dialog is open on a row, or the operator has paged past the first page,
  // which a reload would drop.
  const pollPaused = loading || paging || busyId !== null || removal !== null || reviewing !== null;
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible' || pollPaused || pagedRef.current || readsOutRef.current > 0) {
        return;
      }
      void load({ quiet: true });
    }, POOL_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load, pollPaused]);

  /** A conflict or a vanished line reloads the pool: the row the operator is looking at no longer reflects the truth. */
  const reportFailure = async (err: unknown) => {
    const failure = failureOf(err);
    const said = serverRefusal(err);
    if (failure?.response?.status === 409 || failure?.response?.status === 404) {
      // The reload runs first: a successful load clears the error, which would wipe this one on the way out.
      const reload = await load();
      if (reload === 'superseded') {
        return;
      }
      // A 409 now names which line holds the waybill, or which state the order is in — that sentence is the point.
      const note = reload === 'applied-failure' ? CONFLICT_STALE_ERROR : CONFLICT_ERROR;
      actionErrorShownRef.current = true;
      setError(said ? `${said} ${note}` : note);
      return;
    }
    actionErrorShownRef.current = true;
    setError(said ?? ACTION_ERROR);
  };

  const runAction = async (scanId: string, action: () => Promise<unknown>, announce?: () => void) => {
    if (busyId) {
      return;
    }
    setBusyId(scanId);
    actionErrorShownRef.current = false;
    setError(null);
    try {
      await action();
      await load();
      announce?.();
    } catch (err) {
      await reportFailure(err);
    } finally {
      setBusyId(null);
    }
  };

  /** Matching a suspected order carries the line, which is what the backend writes tracking onto [BR-5/18]. */
  const matchCandidate = (scan: UnrecognizedScan, candidate: UnrecognizedScanSuspectedOrder) => {
    setReviewing(null);
    void runAction(
      scan.id,
      () =>
        client.resolveUnrecognizedScan(scan.id, {
          sku: candidate.sku,
          orderId: candidate.orderId,
          lineNo: candidate.lineNo,
        }),
      () =>
        showSuccess(
          `✓ Matched to Order ${candidate.orderId} · ${productLabelOf(scan)}`,
          matchOutcomeOf(scan, candidate),
        ),
    );
  };

  /** The line the parcel's own number now names, matched in one press (SSD-7442); the line's sku is what the row resolves to. */
  const matchByNumber = (scan: UnrecognizedScan) => {
    const match = scan.numberMatch;
    // The line's own sku, never the product the desk guessed: the row resolves to what that line carries.
    if (!match || match.status !== 'MATCHED' || match.orderId === null || match.lineNo === null || !match.sku) {
      return;
    }
    matchCandidate(scan, { orderId: match.orderId, lineNo: match.lineNo, sku: match.sku, wcStatus: null });
  };

  /** An inbound number is prompted rather than forced, but a malformed one is refused before it is sent [BR-9]. */
  const inboundNoError =
    removal?.reason === UNRECOGNIZED_SCAN_INBOUND_REASON &&
    removal.inboundNo.trim() !== '' &&
    !UNRECOGNIZED_SCAN_INBOUND_NO_PATTERN.test(removal.inboundNo.trim())
      ? INBOUND_NO_ERROR
      : null;
  /** "Other" names nothing on its own, so the sentence beside it is the reason [PD-5/PD-60]. */
  const memoMissing = removal?.reason === UNRECOGNIZED_SCAN_MEMO_REASON && removal.memo.trim() === '';
  const removalBlocked = removal?.reason == null || memoMissing || inboundNoError !== null;

  const confirmRemoval = () => {
    if (removal == null || removal.reason == null || removalBlocked) {
      return;
    }
    const { scan, rows, reason } = removal;
    // The number only travels with the reason that names one, so switching reasons cannot leak a stale document.
    const inboundNo = reason === UNRECOGNIZED_SCAN_INBOUND_REASON ? removal.inboundNo.trim() : '';
    const memo = removal.memo.trim();
    const body = { reason, ...(inboundNo !== '' ? { inboundNo } : {}), ...(memo !== '' ? { memo } : {}) };
    const wholeParcel = rows.length > 1;
    setRemoval(null);
    void runAction(
      scan.id,
      // A whole box leaves in one write, so a parcel of three never comes back as a parcel of one.
      () =>
        wholeParcel
          ? client.removeUnrecognizedParcel(parcelIdOf(scan), body)
          : client.removeUnrecognizedScan(scan.id, body),
      () =>
        showSuccess(
          wholeParcel
            ? `✓ Removed parcel from pool — ${scan.trackingNo ?? scan.code} · ${rows.length} products · ${UNRECOGNIZED_SCAN_REMOVAL_REASON_LABELS[reason].toLowerCase()}`
            : `✓ Removed from pool — ${scan.code} · ${UNRECOGNIZED_SCAN_REMOVAL_REASON_LABELS[reason].toLowerCase()}`,
        ),
    );
  };

  /** The whole open pool the server reports [D-a]: the page is capped, so counting rendered rows under-reports it. */
  const counted = openTotal ?? scans.length;
  const unreadable = readFailed && scans.length === 0;
  const poolCount = unreadable
    ? `Unrecognized product pool · ${ABSENT} items`
    : `Unrecognized product pool · ${counted} ${counted === 1 ? 'item' : 'items'}`;
  /** Only worth saying when the pool outruns the page; an equal or absent total would just repeat the counters. */
  const capNote =
    openTotal !== null && openTotal > scans.length ? `Showing ${scans.length} of ${openTotal} open entries` : null;
  /** How many boxes the rows make up, when the server says: the floor counts parcels, and the rows are their products. */
  const parcelNote =
    openParcels !== null && !unreadable ? `in ${openParcels} ${openParcels === 1 ? 'parcel' : 'parcels'}` : null;

  const candidatesOf = (scan: UnrecognizedScan) => scan.suspectedOrders ?? [];

  const parcels = parcelsOf(scans);
  const parcelOfRow = (scan: UnrecognizedScan) =>
    parcels.find((parcel) => parcel.rows.some((row) => row.id === scan.id));
  const filtering = filter.trim() !== '';
  /** A desk looking for one box types any part of what it reads on it; the filter never asks the server. */
  const shownParcels = filtering
    ? parcels.filter((parcel) => parcel.rows.some((row) => matchesFilter(row, filter)))
    : parcels;

  return (
    // The Admin Ultimate Design on a React-Bootstrap screen (SSD-7511): the shared layer paints the frame, buttons,
    // fields and alerts; the pool keeps its own table (one block per parcel) and reads its tokens from the layer.
    <Container fluid className="ff-ultimate-page wms-pool">
      <div className="pool-top">
        <div className="pool-heading">
          <h2 className="pool-title">Unrecognized Tracking List</h2>
          <p className="pool-sub">
            Parcels the scan desk could not place, one row per product. Each product is matched to an order line, and
            the orders proposed are the <b>Processing</b> ones that carry that product.
          </p>
        </div>
        <div className="pool-tools">
          <input
            type="search"
            className="pool-filter"
            aria-label="Filter parcels"
            placeholder="Filter by waybill, product, SKU, order or memo…"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          />
          <Button
            size="sm"
            variant="outline-secondary"
            aria-label="Refresh the pool"
            disabled={loading}
            onClick={() => void load()}
          >
            <RefreshIcon />
            Refresh
          </Button>
          <Link to={ROUTES.fulfillment.viewOrders} className="btn btn-outline-secondary btn-sm">
            Back to scan desk
          </Link>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="d-flex justify-content-between align-items-center">
          <span>{error}</span>
          {readFailed && (
            <Button size="sm" variant="outline-danger" disabled={loading} onClick={() => void load()}>
              Retry
            </Button>
          )}
        </Alert>
      )}

      {goneHash !== null && goneHash === hash && <Alert variant="info">{LINKED_ROW_GONE}</Alert>}

      <div className="pool-stats">
        <span className="fw-semibold">{poolCount}</span>
        {parcelNote && <span className="text-muted small ms-2">{parcelNote}</span>}
        {capNote && <span className="text-muted small ms-2">{capNote}</span>}
        {filtering && (
          <span className="text-muted small ms-2">
            {shownParcels.length} of {parcels.length} loaded parcels match
            {nextCursor ? ' — parcels past Load more are not searched' : ''}
          </span>
        )}
        {candidatesUnavailable && <span className="pool-warn ms-2">{CANDIDATES_UNAVAILABLE_TEXT}</span>}
      </div>

      {/* One block per parcel: the box's own facts once in its head row, then one row per product the desk named. */}
      <table className="pool-table">
        <colgroup>
          <col className="c-product" />
          <col className="c-qty" />
          <col className="c-cands" />
          <col className="c-actions" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">Product</th>
            <th scope="col">Qty</th>
            <th scope="col">Proposed orders</th>
            <th scope="col">Action</th>
          </tr>
        </thead>
        {shownParcels.map((parcel) => {
          const head = parcel.rows[0];
          if (!head) {
            return null;
          }
          const many = parcel.rows.length > 1;
          return (
            <tbody key={parcel.id} className="pool-parcel">
              <tr className="pool-head">
                <th scope="rowgroup" colSpan={4}>
                  <div className="pool-head-in">
                    <span className="pool-waybill">{head.trackingNo ?? head.code}</span>
                    {head.trackingNo == null && <span className="pool-chip pool-chip-muted">no waybill</span>}
                    {head.orderNo && <span className="pool-chip">Order No {head.orderNo}</span>}
                    {head.temporaryLocation ? (
                      <span className="pool-chip pool-chip-loc" title="Where the desk put the box">
                        at {head.temporaryLocation}
                      </span>
                    ) : (
                      <span className="pool-chip pool-chip-muted">no location</span>
                    )}
                    {head.slackThreadUrl && (
                      <a className="pool-thread" href={head.slackThreadUrl} target="_blank" rel="noreferrer">
                        Slack thread
                      </a>
                    )}
                    <span className="pool-meta">
                      {parcel.rows.length} {many ? 'products' : 'product'} · {head.operator ?? ABSENT} ·{' '}
                      {kstTimestamp(head.occurredAt)}
                    </span>
                    {head.memo && <span className="pool-memo">“{head.memo}”</span>}
                    {many && (
                      <Button
                        size="sm"
                        variant="outline-danger"
                        className="pool-remove-parcel"
                        disabled={busyId !== null}
                        aria-label={`Remove parcel ${head.trackingNo ?? parcel.id}`}
                        onClick={() =>
                          setRemoval({ scan: head, rows: parcel.rows, reason: null, inboundNo: '', memo: '' })
                        }
                      >
                        Remove parcel
                      </Button>
                    )}
                  </div>
                </th>
              </tr>
              {parcel.rows.map((scan) => {
                const candidates = candidatesOf(scan);
                const { shown: shownCandidates, more: moreCandidates } = candidatesForRow(scan);
                const linked = poolRowDomId(scan.id) === linkedRowDomId;
                return (
                  /*
                   * Every row in this pool is open — the emphasis says the entry is still waiting on someone [BR-6].
                   * The one a link named is marked apart, so the operator who followed it lands on the right row.
                   */
                  <tr
                    key={scan.id}
                    id={poolRowDomId(scan.id)}
                    tabIndex={-1}
                    className={`pool-row ${linked ? 'table-info' : 'table-warning'}`}
                    aria-current={linked ? 'true' : undefined}
                  >
                    <td className="pool-product" data-label="Product">
                      {/* Brand first, inherited from the View Orders decision so every screen names goods alike [B]. */}
                      <div className="pool-name">
                        {scan.brand && <b>{scan.brand}</b>} {scan.productName ?? (scan.brand ? '' : ABSENT)}
                      </div>
                      {(scan.productNameKr || scan.size) && (
                        <div className="pool-name-sub">
                          {[scan.productNameKr, scan.size].filter(Boolean).join(' · ')}
                        </div>
                      )}
                      {/* The code the desk scanned; on a parcel registered off its label it is the waybill in the head row. */}
                      {scan.code !== scan.trackingNo && scan.codeKind !== 'WAYBILL' && (
                        <div className="pool-code">{scan.code}</div>
                      )}
                    </td>
                    <td className="pool-qty" data-label="Qty">
                      {scan.quantity ?? ABSENT}
                    </td>
                    <td className="pool-cands" data-label="Proposed orders">
                      {scan.numberMatch && (
                        <div className={`pool-numbermatch pool-numbermatch-${scan.numberMatch.status.toLowerCase()}`}>
                          <span>{numberMatchText(scan.numberMatch)}</span>
                          {scan.numberMatch.status === 'MATCHED' && (
                            <Button
                              size="sm"
                              variant="primary"
                              disabled={busyId !== null || !scan.numberMatch.sku}
                              aria-label={`Match ${scan.code} to Order ${scan.numberMatch.orderId} line ${scan.numberMatch.lineNo}`}
                              onClick={() => matchByNumber(scan)}
                            >
                              Match to this line
                            </Button>
                          )}
                        </div>
                      )}
                      {candidates.length === 0 ? (
                        // A number that already names the line says more than "no Processing order carries it" (SSD-7442).
                        scan.numberMatch?.status !== 'MATCHED' && (
                          <span className="pool-none">
                            {candidatesUnavailable ? CANDIDATES_UNAVAILABLE_TEXT : NO_CANDIDATE_TEXT}
                          </span>
                        )
                      ) : (
                        <ul className="pool-cand-list">
                          {shownCandidates.map((candidate) => (
                            <li key={`${candidate.orderId}-${candidate.lineNo}`}>
                              {candidate.pic && <b>{candidate.pic}</b>}
                              <Link to={ROUTES.fulfillment.orderDetail(candidate.orderId)}>
                                Order {candidate.orderId}
                              </Link>
                              <span className="text-muted">{candidate.channel ?? ABSENT}</span>
                              {/* The shop status says nothing about what the warehouse did with the order [D]. */}
                              <span className="pool-chip pool-chip-muted">
                                {candidate.fulfillmentStatus ?? candidate.wcStatus ?? 'unknown'}
                              </span>
                            </li>
                          ))}
                          {/* The row prints a few; the rest still exist and one of them may be the match. */}
                          {moreCandidates > 0 && (
                            <li className="pool-more">
                              +{moreCandidates} more not listed — see them all in Review &amp; Match
                            </li>
                          )}
                        </ul>
                      )}
                    </td>
                    <td className="pool-actions" data-label="Action">
                      {/* A row nobody proposed an order for is still workable: removing must not be the only way out. */}
                      <Button
                        size="sm"
                        variant={candidates.length > 0 ? 'primary' : 'outline-primary'}
                        disabled={busyId !== null}
                        {...(candidates.length === 0 && {
                          title: candidatesUnavailable ? CANDIDATES_UNAVAILABLE_HINT : NO_CANDIDATE_HINT,
                        })}
                        aria-label={`Review and match ${scan.code}`}
                        onClick={() => setReviewing(scan)}
                      >
                        Review &amp; Match
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        className="pool-remove-row"
                        disabled={busyId !== null}
                        title="Remove this product from the pool"
                        aria-label={`Remove ${scan.code}`}
                        onClick={() => setRemoval({ scan, rows: [scan], reason: null, inboundNo: '', memo: '' })}
                      >
                        <CloseIcon />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          );
        })}
        {!loading && scans.length === 0 && !readFailed && (
          <tbody>
            <tr>
              <td colSpan={4} className="pool-empty">
                No unrecognized scans are waiting.
              </td>
            </tr>
          </tbody>
        )}
        {!loading && scans.length > 0 && shownParcels.length === 0 && (
          <tbody>
            <tr>
              <td colSpan={4} className="pool-empty">
                No loaded parcel matches “{filter.trim()}”.
              </td>
            </tr>
          </tbody>
        )}
      </table>
      {loading && (
        <div className="d-flex justify-content-center py-4">
          <Spinner animation="border" size="sm" />
        </div>
      )}
      {/* [BR-6] the foot repeats the head off the same value, so the two counters cannot disagree. */}
      <div className="pool-foot">
        <div>
          <span>{poolCount}</span>
          {parcelNote && <span className="ms-2">{parcelNote}</span>}
        </div>
        {nextCursor && (
          <Button size="sm" variant="outline-secondary" disabled={paging} onClick={() => void loadMore()}>
            Load more entries
          </Button>
        )}
      </div>

      <Modal
        show={reviewing !== null}
        onHide={() => setReviewing(null)}
        onKeyDown={trapTabFocus}
        centered
        size="lg"
        contentClassName="pool-dialog"
        {...ULTIMATE_MODAL}
      >
        <Modal.Header closeButton>
          <Modal.Title className="pool-dialog-title">
            Review &amp; Match
            {reviewing && (
              <small>
                Parcel <span className="pool-dialog-waybill">{reviewing.trackingNo ?? reviewing.code}</span>
                {positionInParcel(reviewing, parcelOfRow(reviewing))}
              </small>
            )}
          </Modal.Title>
        </Modal.Header>
        {reviewing && (
          <Modal.Body>
            <ParcelCard scan={reviewing} testId="review-summary" />
            {/* Keyed by entry, so candidates paged in for one parcel are never offered for the next. */}
            <ReviewCandidates
              key={`candidates-${reviewing.id}`}
              scan={reviewing}
              client={client}
              candidatesUnavailable={candidatesUnavailable}
              disabled={busyId !== null}
              onMatch={(candidate) => matchCandidate(reviewing, candidate)}
            />
            {/* Keyed by entry, so a lookup typed for one parcel never offers its lines to the next. */}
            <OrderNumberMatch
              key={reviewing.id}
              scan={reviewing}
              client={client}
              disabled={busyId !== null}
              onMatch={(candidate) => matchCandidate(reviewing, candidate)}
            />
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setReviewing(null)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={removal !== null}
        onHide={() => setRemoval(null)}
        onKeyDown={trapTabFocus}
        centered
        contentClassName="pool-dialog"
        {...ULTIMATE_MODAL}
      >
        <Modal.Header closeButton>
          <Modal.Title className="pool-dialog-title">
            {removal && removal.rows.length > 1
              ? 'Remove this parcel from the pool?'
              : 'Remove this item from the pool?'}
            {removal && (
              <small>
                Parcel <span className="pool-dialog-waybill">{removal.scan.trackingNo ?? removal.scan.code}</span>
              </small>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Which parcel is leaving is stated here, so a mis-clicked row is caught before it is confirmed. */}
          {removal && <ParcelCard scan={removal.scan} rows={removal.rows} testId="removal-summary" />}
          <p className="rm-section-sub mt-3">
            It stays on record under the reason you give — it is taken out of the pool, not deleted.
          </p>
          <Form.Group className="mb-3" controlId="removal-reason">
            <Form.Label>Reason</Form.Label>
            <Form.Select
              value={removal?.reason ?? ''}
              onChange={(event) =>
                setRemoval((current) =>
                  current
                    ? { ...current, reason: (event.target.value || null) as UnrecognizedScanRemovalReason | null }
                    : current,
                )
              }
            >
              <option value="">Select a reason…</option>
              {UNRECOGNIZED_SCAN_REMOVAL_REASONS.map((reason) => (
                // The wording is the table's to give, never derived from the constant it stands for.
                <option key={reason} value={reason}>
                  {UNRECOGNIZED_SCAN_REMOVAL_REASON_LABELS[reason]}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {/* Prompted, never forced [BR-9]: an operator without the number to hand can still clear the entry. */}
          {removal?.reason === UNRECOGNIZED_SCAN_INBOUND_REASON && (
            <Form.Group className="mb-3" controlId="removal-inbound-no">
              <Form.Label>Inbound No.</Form.Label>
              <Form.Control
                value={removal.inboundNo}
                maxLength={UNRECOGNIZED_SCAN_INBOUND_NO_LIMIT}
                isInvalid={inboundNoError !== null}
                onChange={(event) =>
                  setRemoval((current) => (current ? { ...current, inboundNo: event.target.value } : current))
                }
              />
              {inboundNoError ? (
                <Form.Control.Feedback type="invalid">{inboundNoError}</Form.Control.Feedback>
              ) : (
                <Form.Text muted>Optional — the issued arrival number, rather than the same fact in prose.</Form.Text>
              )}
            </Form.Group>
          )}
          <Form.Group controlId="removal-memo">
            <Form.Label>Memo (optional · required for &quot;Other&quot;)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Why is this item leaving the pool?"
              value={removal?.memo ?? ''}
              maxLength={UNRECOGNIZED_SCAN_MEMO_LIMIT}
              onChange={(event) =>
                setRemoval((current) => (current ? { ...current, memo: event.target.value } : current))
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setRemoval(null)}>
            Cancel
          </Button>
          <Button variant="danger" disabled={removalBlocked || busyId !== null} onClick={confirmRemoval}>
            Remove from pool
          </Button>
        </Modal.Footer>
      </Modal>
      <FulfillmentToastHost toasts={toasts} onDismiss={dismiss} />
    </Container>
  );
}
